import { Controller } from "@hotwired/stimulus"
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';

export default class extends Controller {
  connect() {
    const isMobile = this.isMobile();
    const rightOptions = isMobile ? 'dayGridDay' : 'dayGridMonth,timeGridWeek,dayGridDay,listWeek';
    this.boundRefresh = this.refresh.bind(this);
    this.tooltips = {};

    this.calendar = new Calendar(this.element, {
      plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
      selectable: true,
      height: '85%',
      timeZone: 'local',
      events: '/app/events.json',
      initialView: isMobile ? 'dayGridDay' : 'timeGridWeek',
      themeSystem: 'standard',
      slotDuration: '00:15:00',
      firstDay: 1,
      nowIndicator: true,
      dayHeaderContent: function(arg) {
        if (arg.view.type === 'dayGridMonth') {
          return arg.text;
        } else {
          return arg.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        }
      },
      businessHours: {
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
        startTime: '07:00',
        endTime: '22:00'
      },
      headerToolbar: {
        left: 'prev,today,next',
        center: isMobile ? null : 'title',
        right: rightOptions
      },
      eventClick: (info) => {
        this.openModal(`/app/events/${info.event.id}/edit`, { source: 'calendar' });
      },
      select: (info) => {
        this.openModal('/app/events/new', { source: 'calendar', start: info.startStr, end: info.endStr });
      },
      eventDidMount: (info) => {
        if (isMobile || !info.event.extendedProps.tooltip) return;

        this.registerTooltip(info);
      },
      eventWillUnmount: (info) => {
        this.unregisterTooltip(info.event.id);
      }
    });

    this.calendar.render();
    window.addEventListener('calendar:update', this.boundRefresh);
  }

  disconnect() {
    window.removeEventListener('calendar:update', this.boundRefresh);
    Object.keys(this.tooltips).forEach((eventId) => this.unregisterTooltip(eventId));
    this.tooltips = {};
  }

  isMobile() {
    const regex = /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    return regex.test(navigator.userAgent);
  }

  refresh() {
    this.calendar.refetchEvents();
  }

  openModal(path, queryParams = {}) {
    const modal = document.querySelector('turbo-frame[id="modal"]');
    if (!modal) return;

    const url = new URL(path, window.location.origin);
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) url.searchParams.set(key, value);
    });
    modal.src = `${url.pathname}?${url.searchParams.toString()}`;
  }

  registerTooltip(info) {
    const showHandler = () => this.showTooltip(info.event.id, info.el, info.event.extendedProps.tooltip)
    const hideHandler = () => this.hideTooltip(info.event.id)

    info.el.addEventListener('mouseenter', showHandler)
    info.el.addEventListener('mouseleave', hideHandler)
    info.el.addEventListener('focusin', showHandler)
    info.el.addEventListener('focusout', hideHandler)

    this.tooltips[info.event.id] = {
      element: info.el,
      showHandler,
      hideHandler,
      tooltipElement: null,
      positionHandler: null
    }
  }

  unregisterTooltip(eventId) {
    const entry = this.tooltips[eventId]
    if (!entry) return

    entry.element.removeEventListener('mouseenter', entry.showHandler)
    entry.element.removeEventListener('mouseleave', entry.hideHandler)
    entry.element.removeEventListener('focusin', entry.showHandler)
    entry.element.removeEventListener('focusout', entry.hideHandler)
    this.hideTooltip(eventId)
    delete this.tooltips[eventId]
  }

  showTooltip(eventId, anchorElement, content) {
    const entry = this.tooltips[eventId]
    if (!entry || entry.tooltipElement) return

    const tooltipElement = document.createElement('div')
    tooltipElement.className = 'tooltip show pointer-events-none fixed z-[120] max-w-xs rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700 shadow-lg'
    tooltipElement.innerHTML = content
    document.body.appendChild(tooltipElement)

    const positionHandler = () => this.positionTooltip(anchorElement, tooltipElement)
    entry.tooltipElement = tooltipElement
    entry.positionHandler = positionHandler

    positionHandler()
    window.addEventListener('scroll', positionHandler, true)
    window.addEventListener('resize', positionHandler)
  }

  hideTooltip(eventId) {
    const entry = this.tooltips[eventId]
    if (!entry || !entry.tooltipElement) return

    window.removeEventListener('scroll', entry.positionHandler, true)
    window.removeEventListener('resize', entry.positionHandler)
    entry.tooltipElement.remove()
    entry.tooltipElement = null
    entry.positionHandler = null
  }

  positionTooltip(anchorElement, tooltipElement) {
    const spacing = 12
    const anchorRect = anchorElement.getBoundingClientRect()
    const tooltipRect = tooltipElement.getBoundingClientRect()
    const centeredLeft = anchorRect.left + (anchorRect.width / 2) - (tooltipRect.width / 2)
    const left = Math.min(
      Math.max(spacing, centeredLeft),
      window.innerWidth - tooltipRect.width - spacing
    )
    const top = Math.max(spacing, anchorRect.top - tooltipRect.height - spacing)

    tooltipElement.style.left = `${left}px`
    tooltipElement.style.top = `${top}px`
  }
}
